import React from "react";
import { useI18n } from "../lib/i18n";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Plus, Trash2, Calculator, ArrowDown, ArrowUp } from "lucide-react";

/**
 * Editor for the per-invoice taxes[] array. Supports multiple IVA rates
 * plus the Spanish special taxes (IEH, ECO, SUGAR, IRPF, RECEQ, OTHER).
 *
 * Props:
 *   value: array of { code, label, rate, base, amount, is_retention, notes }
 *   onChange: (newValue) => void
 *   subtotal, otherCharges, onSubtotalChange, onTotalChange: for live total calc
 */
const TAX_OPTIONS = [
  { code: "IVA21", label: "IVA 21%", rate: 21, is_retention: false },
  { code: "IVA10", label: "IVA 10%", rate: 10, is_retention: false },
  { code: "IVA4", label: "IVA 4%", rate: 4, is_retention: false },
  { code: "IVA0", label: "IVA 0% (exempt)", rate: 0, is_retention: false },
  { code: "IEH", label: "IEH (Electricitat)", rate: 0, is_retention: false },
  { code: "ECO", label: "Punt Verd / Ecoembes", rate: 0, is_retention: false },
  { code: "SUGAR", label: "Impost sucre", rate: 0, is_retention: false },
  { code: "IRPF", label: "IRPF retingut", rate: 15, is_retention: true },
  { code: "RECEQ", label: "Recàrrec d'equivalència", rate: 5.2, is_retention: false },
  { code: "OTHER", label: "Altres impostos", rate: 0, is_retention: false },
];

const codeColor = (code, is_retention) => {
  if (is_retention) return "text-rose-300 border-rose-500/30 bg-rose-500/5";
  if (code.startsWith("IVA")) return "text-blue-300 border-blue-500/30 bg-blue-500/5";
  if (code === "IEH") return "text-amber-300 border-amber-500/30 bg-amber-500/5";
  if (code === "ECO") return "text-emerald-300 border-emerald-500/30 bg-emerald-500/5";
  if (code === "SUGAR") return "text-pink-300 border-pink-500/30 bg-pink-500/5";
  if (code === "RECEQ") return "text-violet-300 border-violet-500/30 bg-violet-500/5";
  return "text-slate-300 border-slate-500/30 bg-slate-500/5";
};

const fmtEur = (v) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
    .format(Number(v) || 0);

export default function TaxesEditor({
  value,
  onChange,
  subtotal = 0,
  otherCharges = 0,
}) {
  const { lang } = useI18n();
  const taxes = Array.isArray(value) ? value : [];

  const setTax = (idx, patch) => {
    const next = taxes.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    onChange(next);
  };

  const addTax = (code) => {
    const opt = TAX_OPTIONS.find((o) => o.code === code) || TAX_OPTIONS[0];
    const newTax = {
      code: opt.code,
      label: opt.label,
      rate: opt.rate,
      base: 0,
      amount: 0,
      is_retention: opt.is_retention,
      notes: "",
    };
    onChange([...taxes, newTax]);
  };

  const removeTax = (idx) => {
    onChange(taxes.filter((_, i) => i !== idx));
  };

  // Compute live total = subtotal + non-retention taxes + other_charges - retentions
  const ivaTotal = taxes
    .filter((t) => !t.is_retention && (t.code || "").startsWith("IVA"))
    .reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const otherTaxTotal = taxes
    .filter((t) => !t.is_retention && !(t.code || "").startsWith("IVA"))
    .reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const retentionTotal = taxes
    .filter((t) => t.is_retention)
    .reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const computedTotal =
    Number(subtotal || 0) + ivaTotal + otherTaxTotal + Number(otherCharges || 0) - retentionTotal;

  return (
    <div className="space-y-3" data-testid="taxes-editor">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-2">
          <Calculator size={12} />
          {lang === "es" ? "Impuestos detectados" : "Impostos detectats"}
          <span className="text-slate-400 font-mono">{taxes.length}</span>
        </div>
        <select
          className="text-xs bg-slate-800 border border-white/10 rounded px-2 py-1 text-white"
          onChange={(e) => { if (e.target.value) { addTax(e.target.value); e.target.value = ""; } }}
          value=""
          data-testid="taxes-add-select"
        >
          <option value="">{lang === "es" ? "+ Añadir impuesto" : "+ Afegir impost"}</option>
          {TAX_OPTIONS.map((o) => (
            <option key={o.code} value={o.code}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Empty state */}
      {taxes.length === 0 && (
        <div className="text-xs text-slate-500 text-center py-3 border border-dashed border-white/10 rounded-lg">
          {lang === "es"
            ? "No se detectaron impuestos. Usa el selector para añadir uno."
            : "No s'han detectat impostos. Usa el selector per afegir-ne un."}
        </div>
      )}

      {/* Tax rows */}
      {taxes.map((t, idx) => (
        <div
          key={idx}
          className={`grid grid-cols-12 gap-2 items-center p-2 rounded-lg border ${codeColor(t.code, t.is_retention)}`}
          data-testid={`tax-row-${idx}`}
        >
          <div className="col-span-3 sm:col-span-2 flex items-center gap-1.5">
            {t.is_retention ? <ArrowDown size={12} className="text-rose-400" /> : <ArrowUp size={12} className="text-emerald-400" />}
            <select
              value={t.code}
              onChange={(e) => {
                const opt = TAX_OPTIONS.find((o) => o.code === e.target.value) || TAX_OPTIONS[0];
                setTax(idx, {
                  code: opt.code,
                  label: opt.label,
                  rate: t.rate || opt.rate,
                  is_retention: opt.is_retention,
                });
              }}
              className="bg-transparent border-0 text-xs font-semibold focus:outline-none w-full"
              data-testid={`tax-code-${idx}`}
            >
              {TAX_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>{o.code}</option>
              ))}
            </select>
          </div>
          <div className="col-span-3 sm:col-span-2">
            <Input
              type="number"
              step="0.01"
              value={t.rate || 0}
              onChange={(e) => setTax(idx, { rate: Number(e.target.value) })}
              className="bg-white/5 border-white/10 font-mono h-8 text-xs"
              placeholder={lang === "es" ? "%" : "%"}
              data-testid={`tax-rate-${idx}`}
            />
          </div>
          <div className="col-span-3 hidden sm:block">
            <Input
              type="number"
              step="0.01"
              value={t.base || 0}
              onChange={(e) => setTax(idx, { base: Number(e.target.value) })}
              className="bg-white/5 border-white/10 font-mono h-8 text-xs"
              placeholder="Base"
              data-testid={`tax-base-${idx}`}
            />
          </div>
          <div className="col-span-4 sm:col-span-3">
            <Input
              type="number"
              step="0.01"
              value={t.amount || 0}
              onChange={(e) => setTax(idx, { amount: Number(e.target.value) })}
              className="bg-white/5 border-white/10 font-mono h-8 text-xs font-bold"
              placeholder="Import €"
              data-testid={`tax-amount-${idx}`}
            />
          </div>
          <div className="col-span-2 flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={() => setTax(idx, { is_retention: !t.is_retention })}
              className="text-[10px] px-1.5 py-0.5 rounded text-slate-400 hover:bg-white/5"
              title={lang === "es" ? "Cambiar a retención" : "Canviar a retenció"}
              data-testid={`tax-toggle-retention-${idx}`}
            >
              {t.is_retention ? "RET" : "+"}
            </button>
            <button
              type="button"
              onClick={() => removeTax(idx)}
              className="p-1 rounded hover:bg-rose-500/20 text-slate-400 hover:text-rose-300"
              data-testid={`tax-delete-${idx}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      ))}

      {/* Live total */}
      <div className="mt-2 p-3 rounded-lg border border-white/10 bg-white/[0.02]">
        <div className="grid grid-cols-2 gap-1.5 text-xs">
          <div className="text-slate-400">{lang === "es" ? "Base imponible" : "Base imposable"}</div>
          <div className="text-right text-slate-200 font-mono">{fmtEur(subtotal)}</div>
          {ivaTotal !== 0 && (
            <>
              <div className="text-slate-400">{lang === "es" ? "IVA total" : "IVA total"}</div>
              <div className="text-right text-emerald-300 font-mono">+{fmtEur(ivaTotal)}</div>
            </>
          )}
          {otherTaxTotal !== 0 && (
            <>
              <div className="text-slate-400">{lang === "es" ? "Otros impuestos" : "Altres impostos"}</div>
              <div className="text-right text-amber-300 font-mono">+{fmtEur(otherTaxTotal)}</div>
            </>
          )}
          {Number(otherCharges) !== 0 && (
            <>
              <div className="text-slate-400">{lang === "es" ? "Otros cargos" : "Altres càrrecs"}</div>
              <div className="text-right text-slate-200 font-mono">+{fmtEur(otherCharges)}</div>
            </>
          )}
          {retentionTotal !== 0 && (
            <>
              <div className="text-slate-400">{lang === "es" ? "Retenciones" : "Retencions"}</div>
              <div className="text-right text-rose-300 font-mono">−{fmtEur(retentionTotal)}</div>
            </>
          )}
          <div className="text-white font-semibold border-t border-white/10 pt-1.5">
            {lang === "es" ? "TOTAL calculado" : "TOTAL calculat"}
          </div>
          <div
            className="text-right text-white font-mono font-bold border-t border-white/10 pt-1.5"
            data-testid="taxes-computed-total"
          >
            {fmtEur(computedTotal)}
          </div>
        </div>
      </div>
    </div>
  );
}
